import { useState, useEffect } from 'react';
import { REGION_OPTIONS, DEFAULT_QUERY_TYPE_OPTIONS } from '../../../constants/escalatedQueriesPage';

/**
 * Hook to manage page state for EscalatedQueries page
 * @returns {Object} Page state and handlers
 */
export const useEscalatedQueriesPageState = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [queries, setQueries] = useState([]);
  const [allQueries, setAllQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    projectName: '',
    queryType: '',
  });
  const [projectOptions, setProjectOptions] = useState([]);
  const [queryTypeOptions, setQueryTypeOptions] = useState(DEFAULT_QUERY_TYPE_OPTIONS);
  const [currentUser, setCurrentUser] = useState(null);
  const [statusSummary, setStatusSummary] = useState({
    submitted: 0,
    open: 0,
    delegated: 0,
    escalated: 0,
    closed: 0,
  });
  const [selectedStatusCard, setSelectedStatusCard] = useState(null);
  const [selectedRegionCard, setSelectedRegionCard] = useState(null);
  const [regionStatusSummary, setRegionStatusSummary] = useState({});
  const [backendCounts, setBackendCounts] = useState(null);
  const [edUsersByRegion, setEdUsersByRegion] = useState({});
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [highlightedRegion, setHighlightedRegion] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const toggleProject = (projectName) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectName)) {
        newSet.delete(projectName);
      } else {
        newSet.add(projectName);
      }
      return newSet;
    });
  };

  const toggleRegion = (regionCode) => {
    const isCurrentlyExpanded = expandedRegions.has(regionCode);

    // Always set as highlighted when clicked
    setHighlightedRegion(regionCode);

    if (isCurrentlyExpanded) {
      // Collapsing region - collapse all projects within this region first
      setExpandedProjects((prevProjects) => {
        const newProjectsSet = new Set();
        // Remove all projects that belong to this region
        Array.from(prevProjects).forEach((projectKey) => {
          if (!projectKey.startsWith(`${regionCode}-`)) {
            newProjectsSet.add(projectKey);
          }
        });
        return newProjectsSet;
      });

      // Then collapse the region
      setExpandedRegions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(regionCode);
        return newSet;
      });
    } else {
      // Expanding region - keep existing project states
      setExpandedRegions((prev) => {
        const newSet = new Set(prev);
        newSet.add(regionCode);
        return newSet;
      });
    }
  };

  // Reset to page 0 when search or filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  return {
    // State
    page,
    rowsPerPage,
    queries,
    allQueries,
    loading,
    total,
    searchQuery,
    filters,
    projectOptions,
    queryTypeOptions,
    currentUser,
    statusSummary,
    selectedStatusCard,
    selectedRegionCard,
    regionStatusSummary,
    backendCounts,
    edUsersByRegion,
    expandedProjects,
    expandedRegions,
    highlightedRegion,
    // Setters
    setPage,
    setRowsPerPage,
    setQueries,
    setAllQueries,
    setLoading,
    setTotal,
    setSearchQuery,
    setFilters,
    setProjectOptions,
    setQueryTypeOptions,
    setCurrentUser,
    setStatusSummary,
    setSelectedStatusCard,
    setSelectedRegionCard,
    setRegionStatusSummary,
    setBackendCounts,
    setEdUsersByRegion,
    setExpandedProjects,
    setExpandedRegions,
    setHighlightedRegion,
    // Handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    toggleProject,
    toggleRegion,
  };
};
